import { useCallback, useEffect, useRef, useState } from "react";

// Generic deadline/elapsed timer engine (lifted from apps/mobile into
// @jackedlogs/ui in Fase 2 so mobile and the future web-rnw logger share ONE
// engine — next/consolidation.md; the legacy apps/web copy is a dead duplicate
// left in place until that logger is retired). Two consumers wrap it:
//
//   - useRestTimer (count-DOWN): the rest between sets. Schedules a notifee
//     cue at the deadline so the bell rings even with the app backgrounded.
//   - the timed-exercise timer (count-UP or count-DOWN): planks and other
//     isometric holds logged in seconds (inbox 3dd20b1c).
//
// Antenore's call (2026-06-05): "rendi e generalizza il codice del rest timer,
// così che si possano impostare countdown e timer default per esercizio e
// globali, come il rest timer." So the engine is mode-agnostic; the
// rest-specific bits (default-resolution chain, exercise slug, notifee channel
// naming) live in the thin adapters, not here.
//
// Pure React — no react-native import — so it runs unchanged on web. Time is
// wall-clock based (Date.now), so the count survives tab/app backgrounding and
// clock skew: down reads ceil((endsAt - now)/1000), up reads
// floor((now - startedAt)/1000). The notifee scheduling stays a caller concern
// (onSchedule/onCancel) — the engine never imports the notification layer.

export type TimerMode = "down" | "up";

export type CountTimerState = {
  mode: TimerMode;
  // down: wall-clock deadline; null when idle/paused. up: always null.
  endsAt: number | null;
  // up: wall-clock start; null when idle/paused. down: always null.
  startedAt: number | null;
  // down: whole seconds left (clamped at 0). up: 0.
  remainingSec: number;
  // up: whole seconds counted so far. down: totalSec - remainingSec, UNLESS
  // the countdown was started with `overtime`, in which case it is the real
  // time held and keeps growing past totalSec (see isOvertime).
  elapsedSec: number;
  // down: the duration the countdown started from. up: the optional target
  // (0 = open-ended stopwatch).
  totalSec: number;
  // The number a banner should show: remaining for down, elapsed for up.
  // An overtime countdown past its deadline shows the OVERSHOOT (the caller
  // renders it with a leading +), not a frozen 0.
  displaySec: number;
  // An overtime countdown that has passed its deadline and is still counting.
  // Always false for a plain countdown and for count-up.
  isOvertime: boolean;
  // The deadline has arrived and the clock is not held at it by a pause. It
  // lives HERE, next to isOvertime, because both are the same kind of thing —
  // a fact about the count, derived from endsAt/remainingSec/isPaused, which
  // this hook already owns. Two consumers watch for it to fire once (the rest
  // timer's grace window, the prescribed bout's self-completion) and each used
  // to re-derive the same three-part boolean; one changed definition of "zero"
  // would then land in one of them and not the other, silently.
  atZero: boolean;
  isPaused: boolean;
  isRunning: boolean;
  // Opaque caller tag (the rest timer stores the exercise slug here so the
  // in-banner ⚙ knows which catalog row to PATCH). Null while idle.
  tag: string | null;
};

export type StartOptions = {
  seconds: number;
  mode?: TimerMode;
  tag?: string;
  // OPT-IN, count-down only (TM-13). A plain countdown measures at most the
  // number it started from: elapsed is derived as totalSec - remainingSec and
  // remaining floors at 0, so an athlete who holds 45s on a 20s countdown can
  // only ever be credited 20 ("il countdown non misura, conta alla rovescia
  // una prescrizione" — inbox 08ec8624). With this flag the countdown also
  // keeps a start instant, so elapsedSec is the REAL time held and goes on
  // past the deadline. ⛔ Left off by useRestTimer on purpose: for rest, zero
  // means finished (useRestTimer.ts:89 reads remainingSec <= 0 as "done"),
  // and nothing about that path changes.
  overtime?: boolean;
};

export type CountTimerHooks = {
  // Called whenever a wall-clock cue should fire at `deadlineMs`: the down
  // deadline, or an up-to-target end (when started with seconds > 0). The
  // adapter wires this to notifee. Open-ended stopwatches never call it.
  onSchedule?: (deadlineMs: number) => void;
  // Called whenever a scheduled cue must be cancelled (reset/pause/restart).
  onCancel?: () => void;
};

export type UseCountTimer = {
  state: CountTimerState;
  start: (opts: StartOptions) => void;
  add: (seconds: number) => void;
  reset: () => void;
  pause: () => void;
  resume: () => void;
};

// Whole seconds run since a wall-clock instant, and its inverse: the instant a
// count that already holds `heldSec` must have started from. Both directions
// live here because pause/resume use them in pairs — capture the seconds, then
// rebuild the instant — and a rounding rule changed on one side only would make
// a paused timer resume a second off.
const elapsedSince = (startedAt: number, nowMs: number): number =>
  Math.max(0, Math.floor((nowMs - startedAt) / 1000));
const startedAtFor = (nowMs: number, heldSec: number): number =>
  nowMs - heldSec * 1000;

export function useCountTimer(hooks: CountTimerHooks = {}): UseCountTimer {
  const [mode, setMode] = useState<TimerMode>("down");
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [totalSec, setTotalSec] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  // down: captured remaining; up: captured elapsed. Non-null === paused.
  const [pausedSec, setPausedSec] = useState<number | null>(null);
  // Overtime countdowns run TWO clocks (a deadline for the cue, a start
  // instant for the measure), so pausing one has to capture both: pausedSec
  // alone would freeze the remaining and silently drop the seconds already
  // held. Null for every non-overtime timer, which is why the rest timer's
  // pause path is untouched.
  const [pausedElapsedSec, setPausedElapsedSec] = useState<number | null>(null);
  const [tag, setTag] = useState<string | null>(null);

  // Keep the latest callbacks in a ref so re-arming the deadline doesn't have
  // to list them as deps (the adapter passes fresh closures each render).
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;

  const isRunning = endsAt !== null || startedAt !== null;

  useEffect(() => {
    if (!isRunning) return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [isRunning]);

  const runningRemainingSec =
    endsAt === null ? 0 : Math.ceil(Math.max(0, endsAt - now) / 1000);
  const runningElapsedSec = startedAt === null ? 0 : elapsedSince(startedAt, now);

  const remainingSec =
    mode === "down"
      ? pausedSec !== null
        ? pausedSec
        : runningRemainingSec
      : 0;
  // A count-down carries a startedAt ONLY when it was started with overtime,
  // so this flag needs no state of its own: the second clock IS the flag.
  const measuresRealTime = startedAt !== null || pausedElapsedSec !== null;
  const elapsedSec =
    mode === "up"
      ? pausedSec !== null
        ? pausedSec
        : runningElapsedSec
      : measuresRealTime
        ? pausedElapsedSec !== null
          ? pausedElapsedSec
          : runningElapsedSec
        : // Plain countdown, unchanged: capped at totalSec by construction.
          Math.max(0, totalSec - remainingSec);
  // Overtime = the deadline is behind us AND we actually overshot it. Both
  // halves earn their place: without `remainingSec === 0` a +10 taken while
  // overrunning would keep the + on a dial that has seconds left to run again,
  // and without the overshoot check the dial would flash "+0:00" at the exact
  // moment it hits zero.
  const isOvertime =
    mode === "down" &&
    measuresRealTime &&
    remainingSec === 0 &&
    elapsedSec > totalSec;
  const atZero = endsAt !== null && remainingSec <= 0 && pausedSec === null;
  const displaySec =
    mode === "down"
      ? isOvertime
        ? elapsedSec - totalSec
        : remainingSec
      : elapsedSec;

  const start = useCallback((opts: StartOptions) => {
    const nextMode = opts.mode ?? "down";
    const seconds = opts.seconds;
    if (!Number.isFinite(seconds) || seconds < 0) return;
    // Down needs a positive duration; up accepts 0 (open-ended stopwatch).
    if (nextMode === "down" && seconds <= 0) return;
    const nowMs = Date.now();
    setMode(nextMode);
    setTotalSec(seconds);
    setPausedSec(null);
    setPausedElapsedSec(null);
    setTag(opts.tag ?? null);
    setNow(nowMs);
    if (nextMode === "down") {
      const target = nowMs + seconds * 1000;
      setEndsAt(target);
      // The second clock an overtime countdown measures from.
      setStartedAt(opts.overtime === true ? nowMs : null);
      hooksRef.current.onSchedule?.(target);
    } else {
      setStartedAt(nowMs);
      setEndsAt(null);
      // Count-up to a target arms a cue at the target; open-ended does not.
      if (seconds > 0) hooksRef.current.onSchedule?.(nowMs + seconds * 1000);
      else hooksRef.current.onCancel?.();
    }
  }, []);

  // ±N seconds. Down: shift the deadline (clamped to now so "+60" past zero is
  // a fresh 60s, never a notifee fired early). Up: nudge the target. Both keep
  // a paused timer's captured value in sync.
  const add = useCallback((seconds: number) => {
    if (!Number.isFinite(seconds) || seconds === 0) return;
    setPausedSec((prev) => (prev !== null ? Math.max(0, prev + seconds) : prev));
    setTotalSec((prev) => Math.max(0, prev + seconds));
    setEndsAt((prev) => {
      if (prev === null) return prev;
      const nowMs = Date.now();
      const base = Math.max(nowMs, prev);
      const next = Math.max(nowMs, base + seconds * 1000);
      hooksRef.current.onSchedule?.(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setEndsAt(null);
    setStartedAt(null);
    setTotalSec(0);
    setPausedSec(null);
    setPausedElapsedSec(null);
    setTag(null);
    hooksRef.current.onCancel?.();
  }, []);

  const pause = useCallback(() => {
    if (endsAt !== null) {
      setPausedSec(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
      setEndsAt(null);
      // Overtime countdown: capture the held time too, or resuming would
      // restart the measure from zero.
      if (startedAt !== null) {
        setPausedElapsedSec(elapsedSince(startedAt, Date.now()));
        setStartedAt(null);
      }
      hooksRef.current.onCancel?.();
      return;
    }
    if (startedAt !== null) {
      setPausedSec(elapsedSince(startedAt, Date.now()));
      setStartedAt(null);
      hooksRef.current.onCancel?.();
    }
  }, [endsAt, startedAt]);

  const resume = useCallback(() => {
    if (pausedSec === null) return;
    const nowMs = Date.now();
    if (mode === "down") {
      // A plain countdown paused at 0 has nothing left to run. An overtime
      // one does: it was paused PAST the deadline, so it resumes counting up.
      if (pausedSec <= 0 && pausedElapsedSec === null) {
        setPausedSec(null);
        return;
      }
      // Past the deadline the target is now: remaining stays 0 and no second
      // cue is armed, the bell already rang.
      const target = pausedSec > 0 ? nowMs + pausedSec * 1000 : nowMs;
      setEndsAt(target);
      setNow(nowMs);
      setPausedSec(null);
      if (pausedElapsedSec !== null) {
        setStartedAt(startedAtFor(nowMs, pausedElapsedSec));
        setPausedElapsedSec(null);
      }
      if (pausedSec > 0) hooksRef.current.onSchedule?.(target);
      return;
    }
    // up: rebuild startedAt so elapsed continues from the captured value.
    setStartedAt(startedAtFor(nowMs, pausedSec));
    setNow(nowMs);
    setPausedSec(null);
    if (totalSec > 0) {
      hooksRef.current.onSchedule?.(nowMs + Math.max(0, totalSec - pausedSec) * 1000);
    }
  }, [pausedSec, pausedElapsedSec, mode, totalSec]);

  return {
    state: {
      mode,
      endsAt,
      startedAt,
      remainingSec,
      elapsedSec,
      totalSec,
      displaySec,
      isOvertime,
      atZero,
      isPaused: pausedSec !== null,
      isRunning,
      tag,
    },
    start,
    add,
    reset,
    pause,
    resume,
  };
}
